import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { menuIcon } from '../../../../../configs/Icons';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../../utils/Button';
import { GetCurrencySymbol } from '../../../../utils/Currency';
import FocusControl from '../../../../utils/FocusControl';
import InputField from '../../../../utils/InputField';
import { GetFloatNumber } from '../../../../utils/NumberFormatter';
import { mkRelativeUrl } from '../../../../utils/URL';
import { handleGetPackagePricingApi, handleRenewSubscription } from '../SubscriptionsDetailsOpe';

function PackageInfo({ packageData, setFormData, formData }) {
    const handleSetPackageInfo = (item) => {
        setFormData((d) => ({ ...d, PriceId: item?.priceId }));
    };
    if (!packageData?.length) {
        return (
            <div className="mt-2">
                <h2 className="mb-2">
                    Pricing: <span className="text-error"> *</span>
                </h2>
                <div className="flex justify-center items-center py-2 bg-secondary-100 rounded-lg my-3">
                    <span className="text-gray-500">No Package Found</span>
                </div>
            </div>
        );
    }
    return (
        <div className="mt-2">
            <h2 className="mb-2">
                Pricing:<span className="text-error"> *</span>
            </h2>
            <ul>
                {packageData?.map((item) => (
                    <li key={item?.packageId} className="bg-secondary-100 rounded-10 mb-2">
                        <button
                            type="button"
                            className="flex justify-between items-center w-full p-3"
                            onClick={() => handleSetPackageInfo(item)}
                        >
                            <div className="flex items-center">
                                <input
                                    checked={formData?.PriceId === item?.priceId}
                                    type="radio"
                                    name="PackageSelection"
                                    onChange={() => handleSetPackageInfo(item)}
                                    className="mr-1 checked:bg-red-700"
                                />
                                <span>{item?.title}</span>
                            </div>
                            <div>
                                <span>{item?.text}</span>
                            </div>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Pricing({ currency, price }) {
    return (
        <div className="flex flex-row justify-start items-center gap-1 text-primary-700 mb-3">
            <span className="whitespace-nowrap">Price:</span>
            <span className="text-primary font-medium">
                {GetCurrencySymbol(currency)}
                {GetFloatNumber(price)}
            </span>
        </div>
    );
}

function ProductDetails({ data }) {
    return (
        <div className="content-part flex flex-row gap-4 items-start overflow-hidden mb-2">
            <div className="w-11 min-w-[44px]  h-11">
                <img src={data?.logoUrl || menuIcon.settingsWL} alt="" className="w-full block" />
            </div>
            <div className="flex flex-col gap-1">
                <div className="flex flex-row items-center gap-3">
                    <span className="inline-block text-primary text-lg font-medium">
                        {data?.applicationTitle}
                    </span>
                </div>
                <p className="text-sm text-primary-700">{data?.description}</p>
            </div>
        </div>
    );
}

function SubscriptionRenewModal({ data, closeModal }) {
    const { setMessage } = useTemplate();
    const [loading, setLoading] = useState(false);
    const [packageData, setPackageData] = useState([]);
    const [isError, setIsError] = useState('');
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        Domain: data?.domain || '',
        SubscriptionId: data?.id || '',
        PriceId: 0,
        Duration: 1
    });
    // handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((d) => ({ ...d, [name]: value }));
    };
    // handler for finish button click
    const handleCreateClick = (e) => {
        setLoading(true);
        e.preventDefault();
        handleRenewSubscription(data?.id, formData, setIsError)
            .then(({ data: resData }) => {
                setLoading(false);
                navigate(mkRelativeUrl(`/billing/invoices/${resData?.invoiceId}/info`));
            })
            .catch((error) => {
                setLoading(false);
                setMessage({ type: 'error', text: error?.toString(0) });
            });
    };
    // get package data
    useEffect(() => {
        handleGetPackagePricingApi(data?.id)
            .then(({ data: resData }) => {
                setPackageData(resData);
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error?.toString(0) });
            });
    }, [data.id, setMessage]);

    // calculate total price after selecting duration
    const totalPrice = packageData?.find((item) => item?.priceId === formData?.PriceId)?.amount;
    // focus control
    FocusControl(isError, setIsError, formData);

    return (
        <div className="overflow-hidden">
            <div className="flex flex-col overflow-hidden transition-all duration-500 relative">
                <ProductDetails data={data?.applicationInfo} />
                <Pricing price={data?.contractPrice} currency={data.contractCurrency} />

                <InputField
                    label="Domain"
                    name="Domain"
                    placeholder="---"
                    formData={formData}
                    onChange={handleChange}
                    readOnly
                />
                <PackageInfo
                    packageData={packageData}
                    formData={formData}
                    setFormData={setFormData}
                />
                <div className="text-sm">
                    Total Price:{' '}
                    <span className="text-primary font-semibold">
                        {GetCurrencySymbol(data.contractCurrency)}
                        {GetFloatNumber(totalPrice || 0)}
                    </span>
                </div>

                <div className="w-full flex justify-end items-center gap-2 mb-2 mt-5">
                    <ButtonCancel title="Cancel" onClick={closeModal} />
                    <ButtonPrimary
                        title="Renew Subscription"
                        loading={loading}
                        onClick={handleCreateClick}
                    />
                </div>
            </div>
        </div>
    );
}

export default SubscriptionRenewModal;
